import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import { useConfiguration, useUpdateConfiguration } from "@/api/configuration";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import SettingsCard from "./SettingsCard";

const schema = z.object({
  defaultLookbackMonths: z
    .number({ error: "Must be a number" })
    .int()
    .min(1, "Must be between 1 and 120 months")
    .max(120, "Must be between 1 and 120 months"),
  defaultForecastHorizonMonths: z
    .number({ error: "Must be a number" })
    .int()
    .min(1, "Must be between 1 and 36 months")
    .max(36, "Must be between 1 and 36 months"),
});

type FormValues = z.infer<typeof schema>;

export default function ForecastingSettings() {
  const { t } = useTranslation();
  const configQuery = useConfiguration();
  const update = useUpdateConfiguration();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      defaultLookbackMonths: 36,
      defaultForecastHorizonMonths: 6,
    },
  });

  useEffect(() => {
    if (configQuery.data) {
      form.reset({
        defaultLookbackMonths: configQuery.data.defaultLookbackMonths,
        defaultForecastHorizonMonths:
          configQuery.data.defaultForecastHorizonMonths,
      });
    }
  }, [configQuery.data, form]);

  const onSubmit = (values: FormValues) => {
    update.mutate(values, {
      onSuccess: () =>
        toast.success(t("settings.saved", { defaultValue: "Saved" })),
      onError: () =>
        toast.error(t("settings.saveFailed", { defaultValue: "Save failed" })),
    });
  };

  return (
    <SettingsCard
      title={t("settings.rail.forecasting")}
      description={t("settings.configuration.forecasting.description")}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="defaultLookbackMonths"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.forecasting.lookbackLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={120}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t("settings.configuration.forecasting.lookbackDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultForecastHorizonMonths"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.configuration.forecasting.horizonLabel")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={36}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                <FormDescription>
                  {t("settings.configuration.forecasting.horizonDescription")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!form.formState.isDirty || update.isPending}
            >
              {update.isPending
                ? t("settings.configuration.saving")
                : t("common.buttons.save")}
            </Button>
          </div>
        </form>
      </Form>
    </SettingsCard>
  );
}
